/**
 * Arrival card families (rules v0.6 §21.2).
 *
 * Note: v0.6 states the base deck totals 48 cards (22 Green, 19 Amber, 7 Red),
 * but its family table sums to 55 (Amber families total 26). The digital
 * edition uses the family table as written; only ~25 cards are drawn per
 * shift, so the deck size does not change play.
 */
import type { Box } from '../types.ts';

export interface Family {
  /** Diagnosis key printed on the front (provisional). */
  key: string;
  /** Short code used for card ids, e.g. MG → MG-03. */
  code: string;
  box: Box;
  copies: number;
  /** Removed at four-seat tables (psychiatry-only). */
  psychOnly?: boolean;
}

export const BASE_FAMILIES: Family[] = [
  { key: 'jazz-hands', code: 'JH', box: 90, copies: 8 },
  { key: 'sock-loss', code: 'SL', box: 90, copies: 4 },
  { key: 'sequins', code: 'SQ', box: 70, copies: 4 },
  { key: 'imminent-arrival', code: 'IA', box: 70, copies: 2 },
  { key: 'enthusiastic-beard', code: 'EB', box: 70, copies: 2 },
  { key: 'very-hairy', code: 'VH', box: 90, copies: 2 },
  { key: 'deja-vu', code: 'DV', box: 90, copies: 4 },
  { key: 'mild-glowing', code: 'MG', box: 70, copies: 5 },
  { key: 'hat-adhesion', code: 'HA', box: 70, copies: 5 },
  { key: 'spontaneous-mime', code: 'SM', box: 70, copies: 4 },
  { key: 'dread-acute', code: 'ED', box: 70, copies: 3, psychOnly: true },
  { key: 'phantom-limb', code: 'PL', box: 50, copies: 2 },
  { key: 'swallowed-ambition', code: 'SA', box: 50, copies: 2 },
  { key: 'phantom-pregnancy', code: 'PP', box: 50, copies: 1, psychOnly: true },
  { key: 'lycanthropy', code: 'LY', box: 70, copies: 3 },
  { key: 'combustion', code: 'SC', box: 90, copies: 2 },
  { key: 'radiant-fever', code: 'RF', box: 70, copies: 1 },
  { key: 'breech', code: 'BE', box: 70, copies: 1 },
  // Supplement (Oct 2026)
  { key: 'persistent-yodel', code: 'PY', box: 90, copies: 3 },
  { key: 'loose-shadow', code: 'LS', box: 70, copies: 2 },
  { key: 'creased-elbows', code: 'CE', box: 70, copies: 2 },
  { key: 'static-hair', code: 'ST', box: 70, copies: 3 },
  { key: 'melancholy-fog', code: 'MF', box: 70, copies: 2 },
  { key: 'stuck-kazoo', code: 'SK', box: 50, copies: 2 },
  { key: 'internal-thunderstorm', code: 'IT', box: 70, copies: 1 },
  { key: 'shadow-detachment', code: 'SD', box: 70, copies: 1 },
  { key: 'acute-origami', code: 'AO', box: 90, copies: 1 },
];

/** Four-seat substitution: psychiatry-only families out; each of these takes two extra copies in their place. */
// Was Jazz Hands + Hat Adhesion; Hat Adhesion's best route is Theatre, which made the Surgeon the
// four-seat bottleneck (D-41). Static Hair keeps an Amber in the swap on a medical route.
export const FOUR_SEAT_EXTRA: string[] = ['jazz-hands', 'static-hair'];

export function familiesFor(fiveSeat: boolean): Family[] {
  if (fiveSeat) return BASE_FAMILIES.map((f) => ({ ...f }));
  return BASE_FAMILIES.filter((f) => !f.psychOnly).map((f) => {
    if (FOUR_SEAT_EXTRA.includes(f.key)) return { ...f, copies: f.copies + 2 };
    return { ...f };
  });
}

/** Special-pile fronts (DESIGN §6.3, D-16..D-18). */
export const SPECIAL_FRONTS = {
  sprocket: { key: 'hat-adhesion', box: 70 as Box, names: ['Barry Sprocket', 'Gary Sprocket'] },
  vip: { key: 'hat-adhesion-tophat', box: 90 as Box, name: 'Tarquin Bellweather-Mayor', flavour: 'Tarquin has worn the hat since his investiture. Since Tuesday he has had no choice.' },
  // D-17: one line each, none of them a hint: which chorister is the odd one out is drawn at random.
  choir: {
    key: 'sync-hiccups', box: 90 as Box, names: ['Ida Warble', 'Percy Warble', 'Constance Trill', 'Albert Descant'],
    flavours: [
      'Ida hiccups on the downbeat. She would like you to know she is a soprano.',
      'Percy is Ida’s brother. He keeps time with his foot.',
      'Constance has brought the sheet music, in case.',
      'Albert takes the low part and has apologised four times.',
    ],
  },
  newborn: { key: 'brand-new-person', name: 'Brand New Person' },
  regional: ['lycanthropy', 'radiant-fever', 'silent-stroke', 'thyroid-storm', 'combustion'],
};
